import { useMemo } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Layers } from 'lucide-react';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert, Banner } from '@vnetwork/ui/feedback';
import type { DrmProbeResult, DrmVariant } from '@/api/aes';
import { fmtBitrate } from '../shared';

const muted = (v: React.ReactNode) => (
  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{v}</span>
);

/** ABR ladder + clear-check gate for the provision wizard's Probe step. */
export function ProbeResultPanel({ probe }: { probe: DrmProbeResult }) {
  const columns = useMemo<ColumnDef<DrmVariant, unknown>[]>(
    () => [
      {
        id: 'resolution',
        header: 'Resolution',
        cell: ({ row }) => (
          <span style={{ fontFamily: 'var(--vn-font-mono)' }}>{row.original.resolution ?? '—'}</span>
        ),
      },
      { id: 'bitrate', header: 'Bitrate', cell: ({ row }) => muted(fmtBitrate(row.original.bandwidth)) },
      {
        id: 'codecs',
        header: 'Codecs',
        cell: ({ row }) => (
          <span style={{ fontFamily: 'var(--vn-font-mono)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            {row.original.codecs ?? '—'}
          </span>
        ),
      },
      { id: 'fps', header: 'FPS', cell: ({ row }) => muted(row.original.frame_rate ?? '—') },
    ],
    [],
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div style={{ display: 'flex', gap: 'var(--vn-space-4)', flexWrap: 'wrap' }}>
        <ProbeFlag ok={probe.is_master} label="Master playlist" failLabel="Single media playlist" />
        <ProbeFlag ok={probe.is_clear} label="Source is CLEAR" failLabel="Source already encrypted" />
      </div>

      {!probe.is_clear && (
        <Banner
          tone="danger"
          title="Source must be a CLEAR (un-encrypted) origin"
          description="sig-packager AES-128 encrypts on the fly — provide an un-encrypted m3u8 origin. You can't re-encrypt an already-protected source."
        />
      )}

      {probe.warnings && probe.warnings.length > 0 && (
        <Alert tone="warning" title="Probe warnings" icon={<AlertTriangle size={16} />}>
          <ul style={{ margin: 0, paddingLeft: 'var(--vn-space-4)' }}>
            {probe.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={probe.variants}
        getRowId={(v) => v.uri}
        dense
        emptyState={<EmptyState icon={<Layers size={20} />} title="No ABR renditions" description="No renditions were detected in the source playlist." />}
      />
    </div>
  );
}

function ProbeFlag({ ok, label, failLabel }: { ok: boolean; label: string; failLabel: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
      {ok ? (
        <CheckCircle2 size={16} color="var(--vn-success-solid)" />
      ) : (
        <XCircle size={16} color="var(--vn-danger-solid)" />
      )}
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>{ok ? label : failLabel}</span>
    </span>
  );
}
